/**
 * Author: Andrey Morozov
 * Confidential. Proprietary code. Do not distribute without authorization.
 *
 * PlaybackQueue.ts — Playbac\1 \2ueue (services/operator-web-app).
 */

/**
 * E3-S1: Jitter-safe playback queue for PCM16 8kHz from /ws/call.
 * E3-S2 T2: Optional output device via setSinkId when supported (Chrome 110+).
 */
const SAMPLE_RATE = 8000;

export interface PlaybackQueueStartOptions {
  /** E3-S2 T2: Preferred output device id for setSinkId (when supported). */
  outputDeviceId?: string | null;
}

export class PlaybackQueue {
  private audioContext: AudioContext | null = null;
  private queue: Float32Array[] = [];
  private maxQueueSamples = SAMPLE_RATE * 2; // 2s max
  private nextStartTime = 0;
  private scheduled = false;

  async start(options?: PlaybackQueueStartOptions): Promise<void> {
    if (typeof window === 'undefined') return;
    if (this.audioContext?.state === 'running') return;
    const Ctx =
      window.AudioContext ||
      (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
    this.audioContext = new Ctx({ sampleRate: SAMPLE_RATE });
    this.nextStartTime = this.audioContext.currentTime;
    this.scheduled = false;
    const deviceId = options?.outputDeviceId;
    if (
      deviceId &&
      typeof (this.audioContext as unknown as { setSinkId?: (id: string) => Promise<void> })
        .setSinkId === 'function'
    ) {
      try {
        await (
          this.audioContext as unknown as { setSinkId: (id: string) => Promise<void> }
        ).setSinkId(deviceId);
      } catch {
        // setSinkId not supported or failed (e.g. Firefox, Safari)
      }
    }
  }

  push(pcm16: ArrayBuffer): void {
    const view = new Int16Array(pcm16);
    const float32 = new Float32Array(view.length);
    for (let i = 0; i < view.length; i++) {
      float32[i] = view[i] / 32768;
    }
    this.queue.push(float32);
    const total = this.queue.reduce((s, a) => s + a.length, 0);
    if (total > this.maxQueueSamples) {
      const toRemove = total - this.maxQueueSamples;
      let removed = 0;
      while (this.queue.length > 0 && removed < toRemove) {
        const first = this.queue[0];
        if (removed + first.length <= toRemove) {
          this.queue.shift();
          removed += first.length;
        } else {
          this.queue[0] = first.subarray(toRemove - removed);
          removed = toRemove;
        }
      }
    }
    this.schedule();
  }

  private schedule(): void {
    if (
      !this.audioContext ||
      this.audioContext.state !== 'running' ||
      this.queue.length === 0 ||
      this.scheduled
    )
      return;
    this.scheduled = true;
    const run = () => {
      if (this.queue.length === 0) {
        this.scheduled = false;
        return;
      }
      const buf = this.queue.shift()!;
      const source = this.audioContext!.createBufferSource();
      const buffer = this.audioContext!.createBuffer(1, buf.length, SAMPLE_RATE);
      buffer.copyToChannel(buf, 0);
      source.buffer = buffer;
      source.connect(this.audioContext!.destination);
      if (this.nextStartTime < this.audioContext!.currentTime) {
        this.nextStartTime = this.audioContext!.currentTime;
      }
      source.start(this.nextStartTime);
      this.nextStartTime += buffer.length / SAMPLE_RATE;
      source.onended = () => {
        this.scheduled = false;
        if (this.queue.length > 0) this.schedule();
      };
    };
    run();
  }

  stop(): void {
    this.queue.length = 0;
    if (this.audioContext) {
      this.audioContext.close().catch(() => {});
      this.audioContext = null;
    }
    this.scheduled = false;
  }
}
